import * as React from "react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { Card, CardContent } from "./card";
import { Badge } from "./badge";

interface ResponsiveTableProps extends React.HTMLAttributes<HTMLTableElement> {
  children: React.ReactNode;
}

interface ResponsiveTableHeaderProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode;
}

interface ResponsiveTableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode;
}

interface ResponsiveTableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  children: React.ReactNode;
  mobileTitle?: string; // Optional title for mobile card view
}

interface ResponsiveTableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  children: React.ReactNode;
  mobileLabel?: string; // Label for mobile view
  mobilePriority?: "high" | "medium" | "low"; // Priority for mobile display
}

interface ResponsiveTableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  children: React.ReactNode;
  mobileLabel?: string; // Label for mobile view
  mobilePriority?: "high" | "medium" | "low"; // Priority for mobile display
}

const ResponsiveTable = React.forwardRef<
  HTMLTableElement,
  ResponsiveTableProps
>(({ className, children, ...props }, ref) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <div className="space-y-3">{children}</div>;
  }

  return (
    <div className="relative w-full overflow-auto">
      <table
        ref={ref}
        className={cn("w-full caption-bottom text-sm", className)}
        {...props}
      >
        {children}
      </table>
    </div>
  );
});
ResponsiveTable.displayName = "ResponsiveTable";

const ResponsiveTableHeader = React.forwardRef<
  HTMLTableSectionElement,
  ResponsiveTableHeaderProps
>(({ className, children, ...props }, ref) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return null; // Headers are not shown in mobile card view
  }

  return (
    <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props}>
      {children}
    </thead>
  );
});
ResponsiveTableHeader.displayName = "ResponsiveTableHeader";

const ResponsiveTableBody = React.forwardRef<
  HTMLTableSectionElement,
  ResponsiveTableBodyProps
>(({ className, children, ...props }, ref) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <div className="space-y-3">{children}</div>;
  }

  return (
    <tbody
      ref={ref}
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    >
      {children}
    </tbody>
  );
});
ResponsiveTableBody.displayName = "ResponsiveTableBody";

const ResponsiveTableRow = React.forwardRef<
  HTMLTableRowElement,
  ResponsiveTableRowProps
>(({ className, children, mobileTitle, ...props }, ref) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Card className="border border-border hover:bg-muted/50 transition-colors">
        <CardContent className="p-4">
          {mobileTitle && (
            <div className="font-semibold text-lg mb-3 text-primary border-b pb-2">
              {mobileTitle}
            </div>
          )}
          <div className="space-y-3">{children}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <tr
      ref={ref}
      className={cn(
        "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
        className,
      )}
      {...props}
    >
      {children}
    </tr>
  );
});
ResponsiveTableRow.displayName = "ResponsiveTableRow";

const ResponsiveTableHead = React.forwardRef<
  HTMLTableCellElement,
  ResponsiveTableHeadProps
>(
  (
    { className, children, mobileLabel, mobilePriority = "medium", ...props },
    ref,
  ) => {
    const isMobile = useIsMobile();

    if (isMobile) {
      return null; // Headers are not shown in mobile card view
    }

    return (
      <th
        ref={ref}
        className={cn(
          "h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
          className,
        )}
        {...props}
      >
        {children}
      </th>
    );
  },
);
ResponsiveTableHead.displayName = "ResponsiveTableHead";

const ResponsiveTableCell = React.forwardRef<
  HTMLTableCellElement,
  ResponsiveTableCellProps
>(
  (
    { className, children, mobileLabel, mobilePriority = "medium", ...props },
    ref,
  ) => {
    const isMobile = useIsMobile();

    if (isMobile) {
      const priorityClasses = {
        high: "text-base font-medium",
        medium: "text-sm",
        low: "text-xs text-muted-foreground",
      };

      return (
        <div
          className={cn(
            "flex justify-between items-center py-1",
            priorityClasses[mobilePriority],
          )}
        >
          {mobileLabel && (
            <span className="font-medium text-muted-foreground mr-2 min-w-0 flex-shrink-0">
              {mobileLabel}:
            </span>
          )}
          <div className="flex-1 text-right min-w-0 break-words">
            {children}
          </div>
        </div>
      );
    }

    return (
      <td
        ref={ref}
        className={cn(
          "p-4 align-middle [&:has([role=checkbox])]:pr-0",
          className,
        )}
        {...props}
      >
        {children}
      </td>
    );
  },
);
ResponsiveTableCell.displayName = "ResponsiveTableCell";

export {
  ResponsiveTable,
  ResponsiveTableHeader,
  ResponsiveTableBody,
  ResponsiveTableRow,
  ResponsiveTableHead,
  ResponsiveTableCell,
};
